import React, { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import SearchNormal from "@assets/svg/search-normal.svg?react";

import {
  FilterContainer,
  DropdownContainer,
  DropdownContent,
  OptionsContainer,
  SearchContainer,
  SearchInput,
  SearchIcon,
  OptionContainer,
  OptionContent,
  OptionText,
  StyledCheckbox,
} from "./styles";
import FilterDropDownBtn from "./FilterDropDownBtn";

export interface ChannelOption {
  key: string;
  label: string;
  icon: React.ReactElement;
}

interface ChannelFilterProps {
  label: string;
  selectedNames: string[];
  onSelect: (keys: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  onSearchChange?: (searchTerm: string) => void; // Add search term callback
  resetSearchTerm?: boolean; // Add reset prop
  style?: React.CSSProperties;
}

const NameFilter: React.FC<ChannelFilterProps> = ({
  label,
  selectedNames,
  onSelect,
  isOpen,
  onToggle,
  onSearchChange,
  resetSearchTerm = false, // Add reset prop with default value
  style,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Reset search term when resetSearchTerm prop changes
  useEffect(() => {
    if (resetSearchTerm) {
      setSearchTerm("");
      // Also trigger the search change callback with empty string
      if (onSearchChange) {
        onSearchChange("");
      }
    }
  }, [resetSearchTerm, onSearchChange]);

  // Debounced function to trigger search
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (onSearchChange) {
        onSearchChange(term);
      }
    }, 1000), // 1000ms delay
    [onSearchChange]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Toggle channel selection
  const handleChannelClick = (key: string) => {
    let newSelected: string[];
    if (selectedNames.includes(key)) {
      newSelected = selectedNames.filter((k) => k !== key);
    } else {
      newSelected = [...selectedNames, key];
    }
    onSelect(newSelected);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    // Trigger debounced search
    debouncedSearch(newSearchTerm);
  };

  // Handle search input focus to keep dropdown open
  const handleSearchFocus = () => {
    // Ensure dropdown stays open when user focuses on search input
    if (!isOpen) {
      onToggle();
    }
  };

  // Check if filter is active (has selected names)
  const isActive = selectedNames.length > 0 || searchTerm.length > 0;

  return (
    <FilterContainer ref={dropdownRef}>
      <FilterDropDownBtn label={label} onClick={onToggle} active={isActive} />
      {/* Dropdown content: Channel filter */}
      {isOpen && (
        <DropdownContainer>
          <DropdownContent>
            <SearchContainer>
              <SearchInput
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
              />
              <SearchIcon>
                <SearchNormal />
              </SearchIcon>
            </SearchContainer>
          </DropdownContent>
        </DropdownContainer>
      )}
    </FilterContainer>
  );
};

export default NameFilter;
